// Libraries
import { Box, Flex, Text } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
// Components
import { Dropdown, Voyage, VoyageInnerList } from '@molecules/index';
// Utils
import { groupTuples } from '@helpers/index';
import text from '@translations/en.json';
// Types
import { VoyageCarouselProps } from './VoyageCarousel.type';

/**
 * A carousel component for displaying grouped voyages with a dropdown option for year selection.
 *
 * @component
 * @param {VoyageCarouselProps} props - The props for the VoyageCarousel component.
 * @param {Grouped<Voyage>} props.voyagesGroupedByYear - An object where years are keys and a list of voyages the value.
 * @param {string} props.selectedVoyageId - (optional) The ID of the currently selected voyage.
 * @param {Function} props.handleClick - The click event handler for selecting a voyage.
 * @param {boolean} props.checkVoyageIsActive - Check if the voyage is active, usually the most recent one.
 * @returns {ReactElement} The rendered VoyageCarousel component.
 */
export const VoyageCarousel = (props: VoyageCarouselProps) => {
  const { voyagesGroupedByYear, selectedVoyageId, handleClick, checkVoyageIsActive, ...styles } = props;

  /**
   * Dropdown options generated from the years in voyagesGroupedByYear.
   * Each option contains a label and value representing the year.
   */
  const dropdownOptions = useMemo(
    () => Object.keys(voyagesGroupedByYear).map((year) => ({ label: year, value: year })),
    [voyagesGroupedByYear]
  );

  const [optionSelected, setOptionSelected] = useState(dropdownOptions[dropdownOptions.length - 1]);

  /**
   * Tuples of voyages grouped by 3 to be displayed in the Carousel.
   */
  const tupleVoyages: Voyage[][] = useMemo(() => {
    const voyages = voyagesGroupedByYear[optionSelected.value];
    return groupTuples<Voyage>(3)(voyages);
  }, [voyagesGroupedByYear, optionSelected]);

  return (
    <Flex direction='column' {...styles}>
      <Flex p={4} justify='space-between'>
        <Text as='h3' textAlign='left' fontSize='xl' fontWeight='medium'>
          {text.voyage_list.voyages}
        </Text>
        <Dropdown options={dropdownOptions} value={optionSelected} onChange={setOptionSelected} />
      </Flex>
      <Box mx='auto' w='full'>
        {dropdownOptions.map((option) => (
          <Box
            key={option.value}
            display={option.value === optionSelected.value ? 'block' : 'none'}
            px={4}
            boxSizing='content-box'
            sx={{
              '.carousel .control-dots': {
                mb: 0,
              },
              '.carousel .control-dots .dot': {
                boxShadow: 'none',
                bg: 'grey.100',
              },
              '.carousel .control-dots .dot.selected': {
                bg: 'base',
              },
            }}
          >
            <Carousel showArrows={false} showThumbs={false} dynamicHeight={false} showStatus={false}>
              {tupleVoyages.map((tuple, index) => (
                <Box key={index} mb={6} textAlign='left'>
                  <VoyageInnerList
                    voyages={tuple}
                    selectedVoyageId={selectedVoyageId}
                    handleClick={handleClick}
                    checkVoyageIsActive={checkVoyageIsActive}
                  />
                </Box>
              ))}
            </Carousel>
          </Box>
        ))}
      </Box>
    </Flex>
  );
};
